import React, { useState, useEffect } from 'react';
import {
    Calendar, Clock, Search, Filter, Star, Users, Heart, Share2,
    ArrowRight, Zap, Award, Flame, LayoutGrid, List, Flower2,
    Leaf, Video, Globe, MapPin, Tag, ChevronDown, Activity,
    Waves, Sparkles, X, Play, TrendingUp
} from 'lucide-react';
import { fetchClassesData } from './fetchClasses';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { SEO } from '../SEO';
import { userApi } from '../../config';

interface ClassPrice {
    session: string;
    monthly: string;
    yearly: string;
}

interface GoogleMeetLink {
    id: string;
    url: string;
    title: string;
    time: string;
}

interface YogaClass {
    _id: string;
    classTitle: string;
    classDescription: string;
    classStartDate: string;
    classEndDate: string;
    classDuration: string;
    classCategory: string;
    SerialCode: string;
    mentorId: string;
    classThumbnail: string;
    googleMeetLinks: GoogleMeetLink[];
    createAt?: string;
    updateAt?: string;
    classPrice: ClassPrice;
    purchaseStatus: 'Free Tier' | 'Premium Tier' | 'Demo Tier';
    pricingType?: string;
    demoDuration?: string;
    instructorName?: string;
    instructorThumbnail: string;
    mentorRating?: number;
    studentsEnrolled?: number;
    maxStudents?: number;
    tags?: string[];
    trending?: boolean;
    featured?: boolean;
}

interface YogaClassApi {
    data: YogaClass[];
    message: string;
}

export default function ClassesDesign() {
    const location = useLocation();
    const navigate = useNavigate();

    const [classes, setClasses] = useState<YogaClass[]>([]);
    const [filteredClasses, setFilteredClasses] = useState<YogaClass[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>('All');
    const [selectedPriceFilter, setSelectedPriceFilter] = useState<string>('All Prices');
    const [selectedLocation, setSelectedLocation] = useState<string>('All Locations');
    const [sortBy, setSortBy] = useState<string>('Most Popular');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => {
        const token = sessionStorage.getItem('userAccessToken') || localStorage.getItem('userAccessToken');
        return Boolean(token && token !== 'undefined' && token !== 'null');
    });

    useEffect(() => {
        const checkLogin = () => {
            const token = sessionStorage.getItem('userAccessToken') || localStorage.getItem('userAccessToken');
            setIsLoggedIn(Boolean(token && token !== 'undefined' && token !== 'null'));
        };
        checkLogin();
        window.addEventListener('storage', checkLogin);
        return () => window.removeEventListener('storage', checkLogin);
    }, []);

    const [favorites, setFavorites] = useState<Set<string>>(() => {
        const token = sessionStorage.getItem('userAccessToken') || localStorage.getItem('userAccessToken');
        const hasAuth = Boolean(token && token !== 'undefined' && token !== 'null');
        if (!hasAuth) return new Set();

        try {
            const raw = localStorage.getItem('metafit_saved_classes');
            if (raw) {
                const list = JSON.parse(raw);
                if (Array.isArray(list)) {
                    return new Set(list.map((c: any) => c._id || c.id).filter(Boolean));
                }
            }
        } catch (e) {
            console.error(e);
        }
        return new Set();
    });

    const [loading, setLoading] = useState<boolean>(true);
    const [availableCategories, setAvailableCategories] = useState<string[]>(['All']);
    const [availablePriceTiers, setAvailablePriceTiers] = useState<string[]>(['All Prices']);
    const [displayedClasses, setDisplayedClasses] = useState<number>(8);

    const enhanceClassData = (classData: YogaClass[]): YogaClass[] => {
        const mentorNames = [
            'Deeksha Sharma', 'Kanak Maheshwari', 'Priya Arya', 'Khushbu dhaked',
            'Sarah Johnson', 'Michael Chen', 'Emma Rodriguez', 'David Kumar'
        ];

        return classData.map((cls, index) => ({
            ...cls,
            instructorName: cls.instructorName || mentorNames[index % mentorNames.length],
            mentorRating: cls.mentorRating || Math.round((4.6 + (index % 4) * 0.1) * 10) / 10,
            studentsEnrolled: cls.studentsEnrolled || Math.floor(Math.random() * 25) + 5,
            maxStudents: cls.maxStudents || 30,
            tags: cls.tags && cls.tags.length > 0 ? cls.tags : getTagsForCategory(cls.classCategory),
            trending: cls.trending !== undefined ? cls.trending : (index % 2 === 0),
            featured: cls.featured !== undefined ? cls.featured : (index % 3 === 0)
        }));
    };

    const getTagsForCategory = (category: string): string[] => {
        const tagMap: Record<string, string[]> = {
            'Meditation & Mindfulness': ['Meditation', 'Mindfulness', 'Relaxation'],
            'Vinyasa': ['Flow', 'Dynamic', 'Strength'],
            'Restorative': ['Gentle', 'Recovery', 'Healing'],
            'Power Yoga': ['Strength', 'Challenge', 'Fitness'],
            'Hatha': ['Foundation', 'Alignment', 'Basics'],
            'Prenatal': ['Prenatal', 'Gentle', 'Safe'],
            'Yin': ['Deep Stretch', 'Flexibility', 'Meditation'],
            'Ashtanga Yoga': ['Ashtanga', 'Strength', 'Weight Loss']
        };
        return tagMap[category] || ['Yoga', 'Wellness'];
    };

    const getPriceTierDisplayName = (tier: string): string => {
        const tierMap: Record<string, string> = {
            'Free Tier': 'Free',
            'Premium Tier': 'Paid',
            'Demo Tier': 'Demo'
        };
        return tierMap[tier] || tier;
    };

    // Handle URL parameters
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const filterParam = searchParams.get('filter');
        if (filterParam === 'free') {
            setSelectedPriceFilter('Free Tier');
        }
    }, [location.search]);

    // Fetch classes data
    useEffect(() => {
        const fetchClasses = async (): Promise<void> => {
            try {
                const data = await fetchClassesData() as YogaClassApi;
                const enhancedData = enhanceClassData(data.data || []);

                const uniqueCategories = Array.from(
                    new Set(enhancedData.map((cls: YogaClass) => cls.classCategory))
                ).filter((cat): cat is string => Boolean(cat));

                const uniquePriceTiers = Array.from(
                    new Set(enhancedData.map((cls: YogaClass) => cls.purchaseStatus))
                ).filter((tier): tier is 'Free Tier' | 'Premium Tier' | 'Demo Tier' => Boolean(tier));

                setAvailableCategories(['All', ...uniqueCategories.sort()]);
                setAvailablePriceTiers(['All Prices', ...uniquePriceTiers.sort()]);
                setClasses(enhancedData);
                setFilteredClasses(enhancedData);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching classes:", error);
                setLoading(false);
            }
        };

        fetchClasses();
    }, []);

    // Filter and sort classes
    useEffect(() => {
        let filtered: YogaClass[] = [...classes];

        // Category filter
        if (selectedCategory !== 'All' && selectedCategory !== 'All Classes') {
            const catLower = selectedCategory.toLowerCase();
            filtered = filtered.filter((cls: YogaClass) => {
                const itemCat = (cls.classCategory || '').toLowerCase();
                const itemTitle = (cls.classTitle || '').toLowerCase();
                const hasTag = cls.tags?.some((t: string) => t.toLowerCase().includes(catLower));
                return itemCat.includes(catLower) || itemTitle.includes(catLower) || hasTag;
            });
        }

        // Price filter
        if (selectedPriceFilter !== 'All Prices') {
            filtered = filtered.filter((cls: YogaClass) => {
                if (selectedPriceFilter === 'Free Tier' || selectedPriceFilter === 'Free') {
                    return cls.purchaseStatus === 'Free Tier' || parseInt(cls.classPrice?.session || '0') === 0;
                }
                if (selectedPriceFilter === 'Premium Tier' || selectedPriceFilter === 'Paid') {
                    return cls.purchaseStatus === 'Premium Tier' || parseInt(cls.classPrice?.session || '0') > 0;
                }
                return cls.purchaseStatus === selectedPriceFilter;
            });
        }

        // Location / Mode filter
        if (selectedLocation !== 'All Locations') {
            filtered = filtered.filter((cls: YogaClass) => {
                const combined = (cls.classTitle + ' ' + cls.classDescription).toLowerCase();
                if (selectedLocation === 'Online') {
                    return combined.includes('online') || (cls.googleMeetLinks && cls.googleMeetLinks.length > 0);
                }
                if (selectedLocation === 'In-Person') {
                    return combined.includes('person') || combined.includes('studio') || combined.includes('offline');
                }
                return true;
            });
        }

        // Search term filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            filtered = filtered.filter((cls: YogaClass) =>
                cls.classTitle.toLowerCase().includes(term) ||
                cls.classDescription.toLowerCase().includes(term) ||
                cls.instructorName?.toLowerCase().includes(term) ||
                cls.classCategory.toLowerCase().includes(term) ||
                cls.tags?.some((tag: string) => tag.toLowerCase().includes(term))
            );
        }

        // Sorting
        if (sortBy === 'Most Popular') {
            filtered.sort((a, b) => (b.studentsEnrolled || 0) - (a.studentsEnrolled || 0));
        } else if (sortBy === 'Highest Rated') {
            filtered.sort((a, b) => (b.mentorRating || 0) - (a.mentorRating || 0));
        } else if (sortBy === 'Price: Low to High') {
            filtered.sort((a, b) => {
                const priceA = parseInt(a.classPrice?.session || a.classPrice?.monthly || '0');
                const priceB = parseInt(b.classPrice?.session || b.classPrice?.monthly || '0');
                return priceA - priceB;
            });
        } else if (sortBy === 'Price: High to Low') {
            filtered.sort((a, b) => {
                const priceA = parseInt(a.classPrice?.session || a.classPrice?.monthly || '0');
                const priceB = parseInt(b.classPrice?.session || b.classPrice?.monthly || '0');
                return priceB - priceA;
            });
        } else if (sortBy === 'Newest') {
            filtered.sort((a, b) => new Date(b.classStartDate || 0).getTime() - new Date(a.classStartDate || 0).getTime());
        }

        setFilteredClasses(filtered);
        setDisplayedClasses(8);
    }, [classes, selectedCategory, selectedPriceFilter, selectedLocation, searchTerm, sortBy]);

    // Sync wishlist from backend if user is logged in
    useEffect(() => {
        const syncWishlist = async () => {
            const token = sessionStorage.getItem('userAccessToken') || localStorage.getItem('userAccessToken');
            if (!token || token === 'undefined' || token === 'null') {
                setFavorites(new Set());
                return;
            }

            try {
                const res = await userApi.get<any>('/wishlist');
                const resData = res.data as any;
                if (resData?.success && Array.isArray(resData.wishlist)) {
                    const backendWishlist: YogaClass[] = resData.wishlist;
                    let localWishlist: YogaClass[] = [];
                    try {
                        const raw = localStorage.getItem('metafit_saved_classes');
                        if (raw) localWishlist = JSON.parse(raw);
                    } catch (e) {}

                    const map = new Map<string, YogaClass>();
                    backendWishlist.forEach((item: any) => map.set(item._id || item.id, item));
                    localWishlist.forEach((item: any) => {
                        const id = item._id || item.id;
                        if (id && !map.has(id)) {
                            map.set(id, item);
                        }
                    });

                    const merged = Array.from(map.values());
                    localStorage.setItem('metafit_saved_classes', JSON.stringify(merged));
                    setFavorites(new Set(merged.map((item: any) => item._id || item.id).filter(Boolean)));

                    if (merged.length > backendWishlist.length) {
                        await userApi.post('/wishlist/sync', { items: merged });
                    }
                }
            } catch (err) {
                console.error("Error syncing wishlist with backend:", err);
            }
        };

        syncWishlist();
    }, []);

    // Toggle favorite with backend sync
    const toggleFavorite = async (classItem: YogaClass): Promise<void> => {
        if (!isLoggedIn) {
            toast.error("Please login to save classes to your wishlist ❤️");
            return;
        }
        const classId = classItem._id;
        const newFavorites = new Set(favorites);
        let updatedList: YogaClass[] = [];
        try {
            const raw = localStorage.getItem('metafit_saved_classes');
            if (raw) {
                updatedList = JSON.parse(raw);
            }
        } catch (e) {
            updatedList = [];
        }

        if (newFavorites.has(classId)) {
            newFavorites.delete(classId);
            updatedList = updatedList.filter((item: any) => (item._id || item.id) !== classId);
            toast.success("Removed from Saved / Wishlist");
        } else {
            newFavorites.add(classId);
            if (!updatedList.some((item: any) => (item._id || item.id) === classId)) {
                updatedList.push(classItem);
            }
            toast.success("Added to Saved / Wishlist ❤️");
        }

        setFavorites(newFavorites);
        localStorage.setItem('metafit_saved_classes', JSON.stringify(updatedList));

        if (sessionStorage.getItem('userAccessToken')) {
            try {
                await userApi.post('/wishlist/toggle', { classItem });
            } catch (err) {
                console.error("Error syncing wishlist toggle with backend:", err);
            }
        }
    };

    // Share link handler
    const handleShare = async (e: React.MouseEvent, classData: YogaClass) => {
        e.preventDefault();
        e.stopPropagation();
        const shareUrl = `${window.location.origin}/class-details/${btoa(btoa(classData._id))}`;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: classData.classTitle,
                    text: `Check out this yoga class: ${classData.classTitle}`,
                    url: shareUrl,
                });
                return;
            } catch (err) {}
        }
        navigator.clipboard.writeText(shareUrl);
        toast.success("Class link copied to clipboard! 📋");
    };

    const handleLoadMore = (): void => {
        setDisplayedClasses(prev => prev + 8);
    };

    const handleResetFilters = (): void => {
        setSearchTerm('');
        setSelectedCategory('All');
        setSelectedPriceFilter('All Prices');
        setSelectedLocation('All Locations');
        setSortBy('Most Popular');
    };

    const formatDate = (dateString: string): string => {
        if (!dateString) return 'Upcoming';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchTerm(event.target.value);
    };

    const handleCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        setSelectedCategory(event.target.value);
    };

    const handlePriceFilterChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        setSelectedPriceFilter(event.target.value);
    };

    // Quick filter categories shown as pills matching the screenshot
    const quickCategories = [
        { label: 'All Classes', value: 'All', icon: LayoutGrid },
        { label: 'Hatha Yoga', value: 'Hatha', icon: Flower2 },
        { label: 'Ashtanga Yoga', value: 'Ashtanga', icon: Activity },
        { label: 'Vinyasa Flow', value: 'Vinyasa', icon: Waves },
        { label: 'Meditation', value: 'Meditation', icon: Leaf },
        { label: 'Weight Loss', value: 'Weight', icon: Flame },
        { label: 'Stress Relief', value: 'Stress', icon: Sparkles },
        { label: 'Beginner Friendly', value: 'Beginner', icon: Users },
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F6F9F5] flex items-center justify-center py-24">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-[#15302B]/20 border-t-[#15302B] rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-[#3B5750] text-lg font-medium">Loading wellness classes...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F6F9F5] text-gray-800 relative pb-20 font-sans">
            <SEO
                title="Live Online Yoga Classes & Daily Wellness Sessions | MetaFit Wellness"
                description="Join interactive live online yoga classes, mindfulness workshops, and daily meditation sessions guided by expert practitioners at MetaFit Wellness."
                canonical="/classes"
                keywords="online yoga classes, live yoga, daily yoga sessions, meditation classes, wellness workshops, MetaFit Wellness"
            />

            {/* ==================== 1. HERO BANNER SECTION ==================== */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 sm:pt-5">
                <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden bg-gradient-to-r from-[#EBF2EB] via-[#F3F7F2] to-[#EEF5EE] border border-[#DEEADE]/70 shadow-sm min-h-0 lg:min-h-[420px] flex flex-col lg:flex-row items-center justify-between">
                    
                    {/* Botanical Watercolor Illustration on Left Corner */}
                    <div className="absolute top-0 left-0 w-36 sm:w-64 md:w-80 h-full pointer-events-none select-none z-0 opacity-50 sm:opacity-75 mix-blend-multiply overflow-hidden">
                        <img
                            src="/images/botanical_leaves.jpg"
                            alt="Botanical Leaves"
                            className="w-full h-full object-cover object-left"
                        />
                    </div>

                    {/* Left Content Column */}
                    <div className="relative z-10 px-4 py-5 sm:px-10 lg:px-12 sm:py-10 lg:py-14 max-w-2xl">
                        {/* Pill Badge */}
                        <div className="inline-flex items-center gap-1.5 px-3 py-0.5 sm:px-3.5 sm:py-1 rounded-full bg-white/80 border border-emerald-800/20 text-[#1E3A34] text-[11px] sm:text-xs font-semibold shadow-xs mb-2 sm:mb-4">
                            <Flower2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-700" />
                            <span>Transform Your Life</span>
                        </div>

                        {/* Large Headline */}
                        <h1 className="text-2xl sm:text-4xl lg:text-6xl font-bold font-serif text-[#15302B] leading-tight tracking-tight mb-1.5 sm:mb-3">
                            Yoga Classes
                        </h1>

                        {/* Subtitle */}
                        <p className="text-[#3B5750] text-xs sm:text-sm lg:text-base leading-snug sm:leading-relaxed mb-3.5 sm:mb-8 max-w-xl">
                            Find the perfect yoga class to balance your mind, body and soul. Join{" "}
                            <span className="font-bold text-[#1E5646]">thousands</span> on their wellness journey.
                        </p>

                        {/* 4 Feature Badges Row */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                            {/* Feature 1 */}
                            <div className="flex items-center gap-2 sm:gap-2.5">
                                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-[#DCEADB] text-[#1E3A34] flex items-center justify-center shrink-0 shadow-xs">
                                    <Leaf className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-800" />
                                </div>
                                <div className="text-[11px] sm:text-xs font-medium text-[#2C453E] leading-tight">
                                    Expert<br />Instructors
                                </div>
                            </div>

                            {/* Feature 2 */}
                            <div className="flex items-center gap-2 sm:gap-2.5">
                                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-[#DCEADB] text-[#1E3A34] flex items-center justify-center shrink-0 shadow-xs">
                                    <Video className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-800" />
                                </div>
                                <div className="text-[11px] sm:text-xs font-medium text-[#2C453E] leading-tight">
                                    Live & In-Person<br />Classes
                                </div>
                            </div>

                            {/* Feature 3 */}
                            <div className="flex items-center gap-2 sm:gap-2.5">
                                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-[#DCEADB] text-[#1E3A34] flex items-center justify-center shrink-0 shadow-xs">
                                    <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-800 fill-emerald-800" />
                                </div>
                                <div className="text-[11px] sm:text-xs font-medium text-[#2C453E] leading-tight">
                                    All Fitness<br />Levels
                                </div>
                            </div>

                            {/* Feature 4 */}
                            <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-[#DCEADB] text-[#1E3A34] flex items-center justify-center shrink-0 shadow-xs">
                                    <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-800" />
                                </div>
                                <div className="text-[11px] sm:text-xs font-medium text-[#2C453E] leading-tight">
                                    Trusted by<br />10K+ Learners
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Image Column: Serene Woman Meditating Outdoors */}
                    <div className="relative w-full lg:w-1/2 h-40 sm:h-72 lg:h-full lg:absolute lg:top-0 lg:right-0 flex items-center justify-end overflow-hidden">
                        <img
                            src="/images/yoga_hero_outdoor.jpg"
                            alt="Yoga Outdoors Meditation"
                            className="w-full h-full object-cover object-center lg:object-right"
                            style={{
                                maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.85) 15%, black 100%)',
                                WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.85) 15%, black 100%)'
                            }}
                        />

                        {/* Script typography overlay: Breathe Move Live Better */}
                        <div className="absolute top-2.5 right-3 sm:top-8 sm:right-10 z-20 text-center select-none pointer-events-none bg-white/40 sm:bg-transparent backdrop-blur-xs sm:backdrop-blur-none px-2 py-1 sm:p-2 rounded-xl">
                            <div className="flex justify-center mb-0.5">
                                <Flower2 className="w-3.5 h-3.5 sm:w-6 sm:h-6 text-[#1E4D3E]" />
                            </div>
                            <p
                                style={{ fontFamily: "'Caveat', cursive", color: '#1E4D3E' }}
                                className="text-lg sm:text-3xl lg:text-[32px] leading-tight font-bold tracking-wide drop-shadow-sm"
                            >
                                Breathe<br />
                                Move<br />
                                Live Better
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* ==================== 2. FLOATING SEARCH & FILTER BAR ==================== */}
            <div className="max-w-6xl mx-auto -mt-4 sm:-mt-8 px-4 relative z-20">
                <div className="bg-white rounded-2xl sm:rounded-full p-2.5 sm:p-3 shadow-xl border border-gray-100 flex flex-col md:flex-row items-center gap-2.5">
                    
                    {/* Search Input */}
                    <div className="flex items-center gap-2.5 flex-1 w-full px-3 py-1.5">
                        <Search className="w-4 h-4 text-gray-400 shrink-0" />
                        <input
                            type="text"
                            placeholder="Search classes, instructors, or techniques..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className="w-full text-xs sm:text-sm text-gray-800 placeholder-gray-400 bg-transparent border-0 focus:outline-none"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="text-gray-400 hover:text-gray-600 text-xs p-1"
                                aria-label="Clear search"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {/* Divider */}
                    <div className="hidden md:block h-6 w-px bg-gray-200" />

                    {/* Category Dropdown */}
                    <div className="relative w-full md:w-auto flex items-center gap-2 px-3 py-1.5">
                        <Globe className="w-4 h-4 text-emerald-700 shrink-0" />
                        <select
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                            className="appearance-none bg-transparent pr-6 text-xs sm:text-sm font-medium text-gray-700 cursor-pointer focus:outline-none w-full md:w-36"
                        >
                            <option value="All">All Categories</option>
                            {availableCategories.filter(c => c !== 'All').map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-3 pointer-events-none" />
                    </div>

                    {/* Divider */}
                    <div className="hidden md:block h-6 w-px bg-gray-200" />

                    {/* Location Dropdown */}
                    <div className="relative w-full md:w-auto flex items-center gap-2 px-3 py-1.5">
                        <MapPin className="w-4 h-4 text-emerald-700 shrink-0" />
                        <select
                            value={selectedLocation}
                            onChange={(e) => setSelectedLocation(e.target.value)}
                            className="appearance-none bg-transparent pr-6 text-xs sm:text-sm font-medium text-gray-700 cursor-pointer focus:outline-none w-full md:w-32"
                        >
                            <option value="All Locations">All Locations</option>
                            <option value="Online">Online Live</option>
                            <option value="In-Person">In-Person Studio</option>
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-3 pointer-events-none" />
                    </div>

                    {/* Divider */}
                    <div className="hidden md:block h-6 w-px bg-gray-200" />

                    {/* Price Dropdown */}
                    <div className="relative w-full md:w-auto flex items-center gap-2 px-3 py-1.5">
                        <Tag className="w-4 h-4 text-emerald-700 shrink-0" />
                        <select
                            value={selectedPriceFilter}
                            onChange={handlePriceFilterChange}
                            className="appearance-none bg-transparent pr-6 text-xs sm:text-sm font-medium text-gray-700 cursor-pointer focus:outline-none w-full md:w-28"
                        >
                            <option value="All Prices">All Prices</option>
                            {availablePriceTiers.filter(p => p !== 'All Prices').map((tier) => (
                                <option key={tier} value={tier}>
                                    {getPriceTierDisplayName(tier)}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-3 pointer-events-none" />
                    </div>

                    {/* Search Classes Button */}
                    <button
                        onClick={() => {}}
                        className="w-full md:w-auto bg-[#15302B] hover:bg-[#1f453e] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-medium text-xs sm:text-sm flex items-center justify-center gap-2 transition-all shadow-md shrink-0 cursor-pointer"
                    >
                        <span>Search Classes</span>
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* ==================== 3. CATEGORY QUICK-FILTER PILLS ==================== */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-7">
                <div className="flex items-center gap-2.5 overflow-x-auto no-scrollbar py-2">
                    {quickCategories.map((tab) => {
                        const Icon = tab.icon;
                        const isActive =
                            (tab.value === 'All' && (selectedCategory === 'All' || selectedCategory === 'All Classes')) ||
                            selectedCategory.toLowerCase().includes(tab.value.toLowerCase()) ||
                            searchTerm.toLowerCase().includes(tab.value.toLowerCase());

                        return (
                            <button
                                key={tab.label}
                                onClick={() => {
                                    if (tab.value === 'All') {
                                        setSelectedCategory('All');
                                        setSearchTerm('');
                                    } else if (tab.value === 'Weight' || tab.value === 'Stress' || tab.value === 'Beginner') {
                                        setSearchTerm(searchTerm.toLowerCase().includes(tab.value.toLowerCase()) ? '' : tab.value);
                                    } else {
                                        setSelectedCategory(selectedCategory === tab.value ? 'All' : tab.value);
                                    }
                                }}
                                className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium flex items-center gap-2 shrink-0 transition-all cursor-pointer ${
                                    isActive
                                        ? 'bg-[#15302B] text-white shadow-sm'
                                        : 'bg-white hover:bg-emerald-50/50 text-[#2C453E] border border-gray-200/90'
                                }`}
                            >
                                <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-emerald-700'}`} />
                                <span>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ==================== 4. SECTION HEADER & VIEW CONTROLS ==================== */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-8 mb-6">
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-bold font-serif text-[#15302B]">Available Classes</h2>
                        <p className="text-xs sm:text-sm text-[#5C746E] mt-0.5">
                            {filteredClasses.length} classes • Updated daily
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Sort by dropdown */}
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                            <span>Sort by:</span>
                            <div className="relative">
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="appearance-none bg-white border border-gray-200 rounded-lg px-3 pr-7 py-1.5 text-xs sm:text-sm font-medium text-gray-700 cursor-pointer focus:outline-none"
                                >
                                    <option value="Most Popular">Most Popular</option>
                                    <option value="Highest Rated">Highest Rated</option>
                                    <option value="Price: Low to High">Price: Low to High</option>
                                    <option value="Price: High to Low">Price: High to Low</option>
                                    <option value="Newest">Newest</option>
                                </select>
                                <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                        </div>

                        {/* Grid & List View Toggle */}
                        <div className="flex items-center bg-gray-100 p-0.5 rounded-lg border border-gray-200/80">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-1.5 rounded-md transition-all cursor-pointer ${
                                    viewMode === 'grid' ? 'bg-[#15302B] text-white shadow-xs' : 'text-gray-500 hover:text-gray-800'
                                }`}
                                title="Grid View"
                            >
                                <LayoutGrid className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-1.5 rounded-md transition-all cursor-pointer ${
                                    viewMode === 'list' ? 'bg-[#15302B] text-white shadow-xs' : 'text-gray-500 hover:text-gray-800'
                                }`}
                                title="List View"
                            >
                                <List className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* ==================== 5. CLASS CARDS LISTING ==================== */}
                {filteredClasses.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm my-6 p-8">
                        <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-800">
                            <Search className="w-8 h-8" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-700 mb-2 font-serif">No classes found</h3>
                        <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                            Try adjusting your search terms or clearing the selected filters to find available sessions.
                        </p>
                        <button
                            onClick={handleResetFilters}
                            className="bg-[#15302B] hover:bg-[#1f453e] text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all shadow-md cursor-pointer"
                        >
                            Reset All Filters
                        </button>
                    </div>
                ) : viewMode === 'grid' ? (
                    /* GRID VIEW (Matching Screenshot) */
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {filteredClasses.slice(0, displayedClasses).map((classData: YogaClass) => (
                            <div
                                key={classData._id}
                                className="bg-white rounded-2xl border border-gray-200/70 shadow-xs hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden group"
                            >
                                {/* Top Image & Badges */}
                                <div className="relative h-44 overflow-hidden bg-gray-100">
                                    <img
                                        src={classData.classThumbnail}
                                        alt={classData.classTitle}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent opacity-60" />

                                    {/* Left Badges (HOT / TOP) */}
                                    <div className="absolute top-3 left-3 flex items-center gap-1.5 z-10">
                                        {classData.trending ? (
                                            <span className="inline-flex items-center gap-1 bg-[#FF6A1A] text-white text-[11px] font-bold px-2.5 py-0.5 rounded-full shadow-sm tracking-wide">
                                                <Flame className="w-3 h-3 fill-white" />
                                                <span>HOT</span>
                                            </span>
                                        ) : classData.featured ? (
                                            <span className="inline-flex items-center gap-1 bg-[#F5A623] text-white text-[11px] font-bold px-2.5 py-0.5 rounded-full shadow-sm tracking-wide">
                                                <Award className="w-3 h-3" />
                                                <span>TOP</span>
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 bg-emerald-700 text-white text-[11px] font-bold px-2.5 py-0.5 rounded-full shadow-sm tracking-wide">
                                                <span>LIVE</span>
                                            </span>
                                        )}
                                    </div>

                                    {/* Right Action Buttons (Heart / Share) */}
                                    <div className="absolute top-3 right-3 flex items-center gap-1.5 z-10">
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleFavorite(classData);
                                            }}
                                            className={`w-7 h-7 rounded-full flex items-center justify-center backdrop-blur-md shadow-xs transition-transform hover:scale-110 cursor-pointer ${
                                                favorites.has(classData._id)
                                                    ? 'bg-white text-red-500'
                                                    : 'bg-white/90 text-gray-600 hover:text-red-500'
                                            }`}
                                            aria-label="Wishlist"
                                        >
                                            <Heart className={`w-3.5 h-3.5 ${favorites.has(classData._id) ? 'fill-red-500 text-red-500' : ''}`} />
                                        </button>
                                        <button
                                            onClick={(e) => handleShare(e, classData)}
                                            className="w-7 h-7 rounded-full bg-white/90 hover:bg-white text-gray-600 hover:text-gray-900 flex items-center justify-center backdrop-blur-md shadow-xs transition-transform hover:scale-110 cursor-pointer"
                                            aria-label="Share"
                                        >
                                            <Share2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>

                                {/* Card Body */}
                                <div className="p-4 sm:p-5 flex flex-col flex-1 justify-between">
                                    <div>
                                        {/* Title & Rating */}
                                        <div className="flex items-start justify-between gap-2 mb-1.5">
                                            <h3 className="text-base font-bold text-[#15302B] leading-snug line-clamp-1 group-hover:text-emerald-800 transition-colors">
                                                {classData.classTitle}
                                            </h3>
                                            {classData.mentorRating && (
                                                <div className="flex items-center gap-1 shrink-0 text-xs font-semibold text-gray-700">
                                                    <Star className="w-3.5 h-3.5 text-[#F5A623] fill-[#F5A623]" />
                                                    <span>{classData.mentorRating.toFixed(1)}</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Category Tag */}
                                        <div className="mb-2">
                                            <span className="inline-block bg-[#E5F3EC] text-[#1E5C49] text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm">
                                                {classData.classCategory || 'YOGA INSTRUCTOR'}
                                            </span>
                                        </div>

                                        {/* Description */}
                                        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mb-4">
                                            {classData.classDescription}
                                        </p>

                                        {/* Instructor & Schedule Info */}
                                        <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100 mb-4">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <div className="w-8 h-8 rounded-full overflow-hidden bg-emerald-100 shrink-0 flex items-center justify-center text-emerald-800 font-bold text-xs ring-1 ring-emerald-200">
                                                    {classData.instructorThumbnail ? (
                                                        <img
                                                            src={classData.instructorThumbnail}
                                                            alt={classData.instructorName}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <span>{(classData.instructorName || 'I').charAt(0).toUpperCase()}</span>
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-xs font-bold text-gray-800 truncate">
                                                        {classData.instructorName || 'Instructor'}
                                                    </p>
                                                    <p className="text-[11px] text-gray-400 leading-none">Instructor</p>
                                                </div>
                                            </div>

                                            <div className="text-right shrink-0">
                                                <div className="inline-flex items-center gap-1 text-[11px] text-gray-600 font-medium">
                                                    <Calendar className="w-3 h-3 text-emerald-600 shrink-0" />
                                                    <span>{formatDate(classData.classStartDate)}</span>
                                                </div>
                                                <div className="flex items-center justify-end gap-1 text-[11px] text-gray-400 mt-0.5">
                                                    <Clock className="w-2.5 h-2.5 text-emerald-600/70 shrink-0" />
                                                    <span>
                                                        {classData.classDuration?.toLowerCase().includes('min')
                                                            ? classData.classDuration
                                                            : `${classData.classDuration} mins`}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Price & Book Now CTA */}
                                    <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                                        <div>
                                            <div className="text-sm font-bold text-[#15302B]">
                                                {classData.purchaseStatus === 'Free Tier' ? (
                                                    <span className="text-emerald-700 font-extrabold">FREE</span>
                                                ) : parseInt(classData.classPrice?.session || '0') === 0 ? (
                                                    <span className="text-emerald-700 font-extrabold">FREE</span>
                                                ) : classData.classPrice?.session ? (
                                                    <span>₹{classData.classPrice.session} <span className="text-xs font-normal text-gray-500">/ Session</span></span>
                                                ) : classData.classPrice?.monthly ? (
                                                    <span>₹{classData.classPrice.monthly} <span className="text-xs font-normal text-gray-500">/ Monthly</span></span>
                                                ) : (
                                                    <span>Paid</span>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-gray-400 leading-none mt-0.5">
                                                {classData.purchaseStatus === 'Free Tier' ? 'complimentary' : 'per session'}
                                            </p>
                                        </div>

                                        <Link
                                            to={`/class-details/${btoa(btoa(classData._id))}`}
                                            style={{ color: '#ffffff' }}
                                            className={`px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer !text-white ${
                                                classData.studentsEnrolled && classData.maxStudents && classData.studentsEnrolled >= classData.maxStudents
                                                    ? 'bg-gray-300 !text-gray-600 cursor-not-allowed'
                                                    : 'bg-[#15302B] hover:bg-[#1f453e] hover:shadow-md'
                                            }`}
                                        >
                                            <span style={{ color: '#ffffff' }}>
                                                {classData.studentsEnrolled && classData.maxStudents && classData.studentsEnrolled >= classData.maxStudents
                                                    ? 'Class Full'
                                                    : 'Book Now'}
                                            </span>
                                            <ArrowRight className="w-3.5 h-3.5 text-white" />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    /* LIST VIEW VARIANT */
                    <div className="flex flex-col gap-4">
                        {filteredClasses.slice(0, displayedClasses).map((classData: YogaClass) => (
                            <div
                                key={classData._id}
                                className="bg-white rounded-2xl border border-gray-200/70 shadow-xs hover:shadow-xl transition-all duration-300 p-4 sm:p-5 flex flex-col md:flex-row items-center justify-between gap-5 group"
                            >
                                <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto flex-1">
                                    <div className="relative w-full sm:w-44 h-36 rounded-xl overflow-hidden shrink-0 bg-gray-100">
                                        <img
                                            src={classData.classThumbnail}
                                            alt={classData.classTitle}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                        <div className="absolute top-2 left-2">
                                            {classData.trending ? (
                                                <span className="inline-flex items-center gap-1 bg-[#FF6A1A] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                                                    <Flame className="w-2.5 h-2.5 fill-white" />
                                                    <span>HOT</span>
                                                </span>
                                            ) : classData.featured ? (
                                                <span className="inline-flex items-center gap-1 bg-[#F5A623] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                                                    <Award className="w-2.5 h-2.5" />
                                                    <span>TOP</span>
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="bg-[#E5F3EC] text-[#1E5C49] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm">
                                                {classData.classCategory || 'YOGA INSTRUCTOR'}
                                            </span>
                                            {classData.mentorRating && (
                                                <div className="flex items-center gap-1 text-xs font-semibold text-gray-700">
                                                    <Star className="w-3.5 h-3.5 text-[#F5A623] fill-[#F5A623]" />
                                                    <span>{classData.mentorRating.toFixed(1)}</span>
                                                </div>
                                            )}
                                        </div>

                                        <h3 className="text-base sm:text-lg font-bold text-[#15302B] leading-snug line-clamp-1 group-hover:text-emerald-800 transition-colors mb-1">
                                            {classData.classTitle}
                                        </h3>

                                        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mb-3 max-w-xl">
                                            {classData.classDescription}
                                        </p>

                                        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full overflow-hidden bg-emerald-100 flex items-center justify-center text-emerald-800 font-bold text-[10px]">
                                                    {classData.instructorThumbnail ? (
                                                        <img src={classData.instructorThumbnail} alt={classData.instructorName} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <span>{(classData.instructorName || 'I').charAt(0).toUpperCase()}</span>
                                                    )}
                                                </div>
                                                <span className="font-semibold text-gray-800">{classData.instructorName || 'Instructor'}</span>
                                            </div>
                                            <div className="flex items-center gap-1 text-gray-500">
                                                <Calendar className="w-3.5 h-3.5 text-emerald-600" />
                                                <span>{formatDate(classData.classStartDate)}</span>
                                            </div>
                                            <div className="flex items-center gap-1 text-gray-500">
                                                <Clock className="w-3.5 h-3.5 text-emerald-600" />
                                                <span>
                                                    {classData.classDuration?.toLowerCase().includes('min')
                                                        ? classData.classDuration
                                                        : `${classData.classDuration} mins`}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex md:flex-col items-center md:items-end justify-between w-full md:w-auto shrink-0 gap-3 pt-3 md:pt-0 border-t md:border-t-0 border-gray-100">
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleFavorite(classData);
                                            }}
                                            className={`w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center transition-transform hover:scale-110 cursor-pointer ${
                                                favorites.has(classData._id)
                                                    ? 'bg-red-50 text-red-500 border-red-200'
                                                    : 'bg-white text-gray-600 hover:text-red-500'
                                            }`}
                                            aria-label="Wishlist"
                                        >
                                            <Heart className={`w-4 h-4 ${favorites.has(classData._id) ? 'fill-red-500 text-red-500' : ''}`} />
                                        </button>
                                        <button
                                            onClick={(e) => handleShare(e, classData)}
                                            className="w-8 h-8 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-900 flex items-center justify-center transition-transform hover:scale-110 cursor-pointer"
                                            aria-label="Share"
                                        >
                                            <Share2 className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div className="text-right">
                                        <div className="text-base font-bold text-[#15302B]">
                                            {classData.purchaseStatus === 'Free Tier' ? (
                                                <span className="text-emerald-700 font-extrabold">FREE</span>
                                            ) : parseInt(classData.classPrice?.session || '0') === 0 ? (
                                                <span className="text-emerald-700 font-extrabold">FREE</span>
                                            ) : classData.classPrice?.session ? (
                                                <span>₹{classData.classPrice.session} <span className="text-xs font-normal text-gray-500">/ Session</span></span>
                                            ) : classData.classPrice?.monthly ? (
                                                <span>₹{classData.classPrice.monthly} <span className="text-xs font-normal text-gray-500">/ Monthly</span></span>
                                            ) : (
                                                <span>Paid</span>
                                            )}
                                        </div>
                                    </div>

                                    <Link
                                        to={`/class-details/${btoa(btoa(classData._id))}`}
                                        style={{ color: '#ffffff' }}
                                        className={`px-5 py-2.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer !text-white ${
                                            classData.studentsEnrolled && classData.maxStudents && classData.studentsEnrolled >= classData.maxStudents
                                                ? 'bg-gray-300 !text-gray-600 cursor-not-allowed'
                                                : 'bg-[#15302B] hover:bg-[#1f453e] hover:shadow-md'
                                        }`}
                                    >
                                        <span style={{ color: '#ffffff' }}>
                                            {classData.studentsEnrolled && classData.maxStudents && classData.studentsEnrolled >= classData.maxStudents
                                                ? 'Class Full'
                                                : 'Book Now'}
                                        </span>
                                        <ArrowRight className="w-3.5 h-3.5 text-white" />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ==================== 6. LOAD MORE BUTTON ==================== */}
                {filteredClasses.length > displayedClasses && (
                    <div className="text-center mt-12">
                        <button
                            onClick={handleLoadMore}
                            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white border border-gray-200/90 rounded-full text-sm font-semibold text-[#15302B] hover:bg-emerald-50/50 hover:border-emerald-300 shadow-sm transition-all duration-300 hover:scale-105 cursor-pointer"
                        >
                            <span>Load More Classes</span>
                            <ArrowRight className="w-4 h-4 text-emerald-700" />
                        </button>
                    </div>
                )}
            </div>

            {/* ==================== 7. ETHEREAL STATS SECTION ==================== */}
            <section className="relative mt-20 pt-10 pb-16 overflow-hidden">
                {/* Background flowing curves and ambient lighting */}
                <div className="absolute inset-0 pointer-events-none select-none">
                    <svg className="w-full h-full opacity-60" viewBox="0 0 1440 320" preserveAspectRatio="none" fill="none">
                        <path
                            d="M-100,160 C320,60 720,260 1540,120"
                            stroke="#3F6652"
                            strokeWidth="1.2"
                            strokeOpacity="0.18"
                        />
                        <path
                            d="M-60,200 C360,100 760,290 1580,150"
                            stroke="#3F6652"
                            strokeWidth="1"
                            strokeOpacity="0.12"
                        />
                    </svg>

                    {/* Floating ambient botanical leaves */}
                    <div className="absolute -top-4 left-4 w-44 h-44 opacity-25 mix-blend-multiply blur-[0.5px]">
                        <img src="/images/botanical_leaves.jpg" alt="" className="w-full h-full object-contain -rotate-12" />
                    </div>
                    <div className="absolute top-1/2 left-[18%] -translate-y-1/2 opacity-35 text-emerald-800 rotate-12">
                        <Leaf className="w-6 h-6 fill-emerald-800/15" />
                    </div>
                    <div className="absolute top-8 right-[32%] opacity-35 text-emerald-800 rotate-45">
                        <Leaf className="w-5 h-5 fill-emerald-800/15" />
                    </div>
                    <div className="absolute bottom-8 right-[18%] opacity-35 text-emerald-800 -rotate-45">
                        <Leaf className="w-6 h-6 fill-emerald-800/15" />
                    </div>
                    <div className="absolute -bottom-4 right-4 w-48 h-48 opacity-25 mix-blend-multiply blur-[0.5px]">
                        <img src="/images/botanical_leaves.jpg" alt="" className="w-full h-full object-contain rotate-145" />
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        
                        {/* --- Card 1: Active Students (Green) --- */}
                        <div className="relative bg-white/95 backdrop-blur-md rounded-[26px] p-6 sm:p-7 shadow-[0_12px_36px_-10px_rgba(5,150,105,0.14)] border border-emerald-100/70 overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_-10px_rgba(5,150,105,0.22)] transition-all duration-300">
                            {/* Top-Right Micro Trend Pill */}
                            <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-emerald-50/90 border border-emerald-100 flex items-center justify-center text-emerald-600 shadow-2xs">
                                <TrendingUp className="w-3.5 h-3.5" />
                            </div>

                            {/* Top Icon & Numbers Row */}
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-[#059669] to-[#10B981] flex items-center justify-center text-white shadow-lg shadow-emerald-500/30 shrink-0">
                                    <Users className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-3xl font-extrabold text-[#15302B] leading-none tracking-tight">2.5K+</h3>
                                    <p className="text-xs sm:text-sm font-medium text-[#526E65] mt-1.5">Active Students</p>
                                </div>
                            </div>

                            {/* Growth Tag Pill */}
                            <div className="mt-5">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200/50">
                                    <TrendingUp className="w-3 h-3 text-emerald-600" />
                                    <span>+12% this month</span>
                                </span>
                            </div>

                            {/* Bottom Wave & Translucent Lotus Flower Illustration */}
                            <div className="absolute -bottom-1 left-0 right-0 h-14 pointer-events-none overflow-hidden">
                                <svg viewBox="0 0 300 60" preserveAspectRatio="none" className="w-full h-full opacity-40">
                                    <path d="M0,40 C60,20 120,48 180,32 C240,16 280,35 300,28 L300,60 L0,60 Z" fill="#A7F3D0" />
                                    <path d="M0,48 C80,35 150,55 220,40 C270,30 290,45 300,42 L300,60 L0,60 Z" fill="#6EE7B7" opacity="0.6" />
                                </svg>
                            </div>
                            <div className="absolute bottom-1 right-2 pointer-events-none opacity-45">
                                <svg viewBox="0 0 100 75" className="w-20 h-16" fill="none">
                                    <path d="M50 12 C44 32 44 48 50 64 C56 48 56 32 50 12 Z" fill="#059669" fillOpacity="0.15" stroke="#059669" strokeWidth="1.4" strokeOpacity="0.45" />
                                    <path d="M50 22 C36 34 36 50 50 64 C42 50 44 34 50 22 Z" fill="#059669" fillOpacity="0.12" stroke="#059669" strokeWidth="1.3" strokeOpacity="0.4" />
                                    <path d="M50 22 C64 34 64 50 50 64 C58 50 56 34 50 22 Z" fill="#059669" fillOpacity="0.12" stroke="#059669" strokeWidth="1.3" strokeOpacity="0.4" />
                                    <path d="M50 34 C26 42 24 57 50 64 C33 57 34 44 50 34 Z" fill="#059669" fillOpacity="0.08" stroke="#059669" strokeWidth="1.2" strokeOpacity="0.3" />
                                    <path d="M50 34 C74 42 76 57 50 64 C67 57 66 44 50 34 Z" fill="#059669" fillOpacity="0.08" stroke="#059669" strokeWidth="1.2" strokeOpacity="0.3" />
                                </svg>
                            </div>
                        </div>

                        {/* --- Card 2: Classes Available (Blue) --- */}
                        <div className="relative bg-white/95 backdrop-blur-md rounded-[26px] p-6 sm:p-7 shadow-[0_12px_36px_-10px_rgba(37,99,235,0.14)] border border-blue-100/70 overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_-10px_rgba(37,99,235,0.22)] transition-all duration-300">
                            {/* Top-Right Micro Trend Pill */}
                            <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-blue-50/90 border border-blue-100 flex items-center justify-center text-blue-600 shadow-2xs">
                                <TrendingUp className="w-3.5 h-3.5" />
                            </div>

                            {/* Top Icon & Numbers Row */}
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-[#2563EB] to-[#38BDF8] flex items-center justify-center text-white shadow-lg shadow-blue-500/30 shrink-0">
                                    <Play className="w-6 h-6 text-white fill-white ml-0.5" />
                                </div>
                                <div>
                                    <h3 className="text-3xl font-extrabold text-[#15302B] leading-none tracking-tight">150+</h3>
                                    <p className="text-xs sm:text-sm font-medium text-[#526E65] mt-1.5">Classes Available</p>
                                </div>
                            </div>

                            {/* Growth Tag Pill */}
                            <div className="mt-5">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-200/50">
                                    <TrendingUp className="w-3 h-3 text-blue-600" />
                                    <span>+8% this month</span>
                                </span>
                            </div>

                            {/* Bottom Wave & Translucent Lotus Flower Illustration */}
                            <div className="absolute -bottom-1 left-0 right-0 h-14 pointer-events-none overflow-hidden">
                                <svg viewBox="0 0 300 60" preserveAspectRatio="none" className="w-full h-full opacity-40">
                                    <path d="M0,40 C60,20 120,48 180,32 C240,16 280,35 300,28 L300,60 L0,60 Z" fill="#BAE6FD" />
                                    <path d="M0,48 C80,35 150,55 220,40 C270,30 290,45 300,42 L300,60 L0,60 Z" fill="#7DD3FC" opacity="0.6" />
                                </svg>
                            </div>
                            <div className="absolute bottom-1 right-2 pointer-events-none opacity-45">
                                <svg viewBox="0 0 100 75" className="w-20 h-16" fill="none">
                                    <path d="M50 12 C44 32 44 48 50 64 C56 48 56 32 50 12 Z" fill="#2563EB" fillOpacity="0.15" stroke="#2563EB" strokeWidth="1.4" strokeOpacity="0.45" />
                                    <path d="M50 22 C36 34 36 50 50 64 C42 50 44 34 50 22 Z" fill="#2563EB" fillOpacity="0.12" stroke="#2563EB" strokeWidth="1.3" strokeOpacity="0.4" />
                                    <path d="M50 22 C64 34 64 50 50 64 C58 50 56 34 50 22 Z" fill="#2563EB" fillOpacity="0.12" stroke="#2563EB" strokeWidth="1.3" strokeOpacity="0.4" />
                                    <path d="M50 34 C26 42 24 57 50 64 C33 57 34 44 50 34 Z" fill="#2563EB" fillOpacity="0.08" stroke="#2563EB" strokeWidth="1.2" strokeOpacity="0.3" />
                                    <path d="M50 34 C74 42 76 57 50 64 C67 57 66 44 50 34 Z" fill="#2563EB" fillOpacity="0.08" stroke="#2563EB" strokeWidth="1.2" strokeOpacity="0.3" />
                                </svg>
                            </div>
                        </div>

                        {/* --- Card 3: Average Rating (Amber) --- */}
                        <div className="relative bg-white/95 backdrop-blur-md rounded-[26px] p-6 sm:p-7 shadow-[0_12px_36px_-10px_rgba(217,119,6,0.14)] border border-amber-100/70 overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_-10px_rgba(217,119,6,0.22)] transition-all duration-300">
                            {/* Top-Right Micro Trend Pill */}
                            <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-amber-50/90 border border-amber-100 flex items-center justify-center text-amber-600 shadow-2xs">
                                <TrendingUp className="w-3.5 h-3.5" />
                            </div>

                            {/* Top Icon & Numbers Row */}
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-[#D97706] to-[#FBBF24] flex items-center justify-center text-white shadow-lg shadow-amber-500/30 shrink-0">
                                    <Star className="w-6 h-6 text-white stroke-[2.2]" />
                                </div>
                                <div>
                                    <h3 className="text-3xl font-extrabold text-[#15302B] leading-none tracking-tight">4.9</h3>
                                    <p className="text-xs sm:text-sm font-medium text-[#526E65] mt-1.5">Average Rating</p>
                                </div>
                            </div>

                            {/* Growth Tag Pill */}
                            <div className="mt-5">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-200/50">
                                    <TrendingUp className="w-3 h-3 text-amber-600" />
                                    <span>+0.3 this month</span>
                                </span>
                            </div>

                            {/* Bottom Wave & Translucent Lotus Flower Illustration */}
                            <div className="absolute -bottom-1 left-0 right-0 h-14 pointer-events-none overflow-hidden">
                                <svg viewBox="0 0 300 60" preserveAspectRatio="none" className="w-full h-full opacity-40">
                                    <path d="M0,40 C60,20 120,48 180,32 C240,16 280,35 300,28 L300,60 L0,60 Z" fill="#FDE68A" />
                                    <path d="M0,48 C80,35 150,55 220,40 C270,30 290,45 300,42 L300,60 L0,60 Z" fill="#FCD34D" opacity="0.6" />
                                </svg>
                            </div>
                            <div className="absolute bottom-1 right-2 pointer-events-none opacity-45">
                                <svg viewBox="0 0 100 75" className="w-20 h-16" fill="none">
                                    <path d="M50 12 C44 32 44 48 50 64 C56 48 56 32 50 12 Z" fill="#D97706" fillOpacity="0.15" stroke="#D97706" strokeWidth="1.4" strokeOpacity="0.45" />
                                    <path d="M50 22 C36 34 36 50 50 64 C42 50 44 34 50 22 Z" fill="#D97706" fillOpacity="0.12" stroke="#D97706" strokeWidth="1.3" strokeOpacity="0.4" />
                                    <path d="M50 22 C64 34 64 50 50 64 C58 50 56 34 50 22 Z" fill="#D97706" fillOpacity="0.12" stroke="#D97706" strokeWidth="1.3" strokeOpacity="0.4" />
                                    <path d="M50 34 C26 42 24 57 50 64 C33 57 34 44 50 34 Z" fill="#D97706" fillOpacity="0.08" stroke="#D97706" strokeWidth="1.2" strokeOpacity="0.3" />
                                    <path d="M50 34 C74 42 76 57 50 64 C67 57 66 44 50 34 Z" fill="#D97706" fillOpacity="0.08" stroke="#D97706" strokeWidth="1.2" strokeOpacity="0.3" />
                                </svg>
                            </div>
                        </div>

                        {/* --- Card 4: Expert Instructors (Orange / Coral) --- */}
                        <div className="relative bg-white/95 backdrop-blur-md rounded-[26px] p-6 sm:p-7 shadow-[0_12px_36px_-10px_rgba(234,88,12,0.14)] border border-orange-100/70 overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_-10px_rgba(234,88,12,0.22)] transition-all duration-300">
                            {/* Top-Right Micro Trend Pill */}
                            <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-orange-50/90 border border-orange-100 flex items-center justify-center text-orange-600 shadow-2xs">
                                <TrendingUp className="w-3.5 h-3.5" />
                            </div>

                            {/* Top Icon & Numbers Row */}
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-[#EA580C] to-[#FB923C] flex items-center justify-center text-white shadow-lg shadow-orange-500/30 shrink-0">
                                    <Award className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-3xl font-extrabold text-[#15302B] leading-none tracking-tight">50+</h3>
                                    <p className="text-xs sm:text-sm font-medium text-[#526E65] mt-1.5">Expert Instructors</p>
                                </div>
                            </div>

                            {/* Growth Tag Pill */}
                            <div className="mt-5">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-semibold border border-orange-200/50">
                                    <TrendingUp className="w-3 h-3 text-orange-600" />
                                    <span>+5 this month</span>
                                </span>
                            </div>

                            {/* Bottom Wave & Translucent Lotus Flower Illustration */}
                            <div className="absolute -bottom-1 left-0 right-0 h-14 pointer-events-none overflow-hidden">
                                <svg viewBox="0 0 300 60" preserveAspectRatio="none" className="w-full h-full opacity-40">
                                    <path d="M0,40 C60,20 120,48 180,32 C240,16 280,35 300,28 L300,60 L0,60 Z" fill="#FED7AA" />
                                    <path d="M0,48 C80,35 150,55 220,40 C270,30 290,45 300,42 L300,60 L0,60 Z" fill="#FDBA74" opacity="0.6" />
                                </svg>
                            </div>
                            <div className="absolute bottom-1 right-2 pointer-events-none opacity-45">
                                <svg viewBox="0 0 100 75" className="w-20 h-16" fill="none">
                                    <path d="M50 12 C44 32 44 48 50 64 C56 48 56 32 50 12 Z" fill="#EA580C" fillOpacity="0.15" stroke="#EA580C" strokeWidth="1.4" strokeOpacity="0.45" />
                                    <path d="M50 22 C36 34 36 50 50 64 C42 50 44 34 50 22 Z" fill="#EA580C" fillOpacity="0.12" stroke="#EA580C" strokeWidth="1.3" strokeOpacity="0.4" />
                                    <path d="M50 22 C64 34 64 50 50 64 C58 50 56 34 50 22 Z" fill="#EA580C" fillOpacity="0.12" stroke="#EA580C" strokeWidth="1.3" strokeOpacity="0.4" />
                                    <path d="M50 34 C26 42 24 57 50 64 C33 57 34 44 50 34 Z" fill="#EA580C" fillOpacity="0.08" stroke="#EA580C" strokeWidth="1.2" strokeOpacity="0.3" />
                                    <path d="M50 34 C74 42 76 57 50 64 C67 57 66 44 50 34 Z" fill="#EA580C" fillOpacity="0.08" stroke="#EA580C" strokeWidth="1.2" strokeOpacity="0.3" />
                                </svg>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ==================== 7. FLOATING ACTION BUTTON (SAVED / WISHLIST) ==================== */}
            {isLoggedIn && (
                <div className="fixed bottom-8 right-8 z-50">
                    <button
                        onClick={() => navigate('/profile?tab=wishlist')}
                        className="group relative w-14 h-14 bg-[#15302B] hover:bg-[#1f453e] rounded-full shadow-2xl transition-all hover:scale-110 active:scale-95 cursor-pointer flex items-center justify-center text-white"
                        aria-label="View favorites"
                        title="View Saved Wishlist"
                    >
                        <Heart className="w-5 h-5 fill-current text-white" />
                        {favorites.size > 0 && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF6A1A] rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md">
                                {favorites.size}
                            </div>
                        )}
                    </button>
                </div>
            )}

            <Toaster position="top-right" />

            <style>{`
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .no-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                .line-clamp-1 {
                    display: -webkit-box;
                    -webkit-line-clamp: 1;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                .line-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
            `}</style>
        </div>
    );
}
